import { Zap, Wrench, Flame, Droplets, Home, Shield, Clock, DollarSign, Award, Phone } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const ServicesSection = () => {
  const { t, language } = useLanguage();
  
  const services = [
    { 
      icon: Zap, 
      titleEn: "Emergency Plumbing", 
      titleEs: "Plomería de Emergencia",
      descEn: "24/7 emergency response for urgent plumbing issues",
      descEs: "Respuesta de emergencia 24/7 para problemas urgentes"
    },
    { 
      icon: Wrench, 
      titleEn: "Pipe Repair & Replacement", 
      titleEs: "Reparación de Tuberías",
      descEn: "Expert pipe repair, replacement and re-piping services",
      descEs: "Reparación, reemplazo y re-piping de tuberías"
    },
    { 
      icon: Flame, 
      titleEn: "Water Heater Installation", 
      titleEs: "Instalación de Calentadores",
      descEn: "Installation and repair of electric and gas water heaters",
      descEs: "Instalación y reparación de calentadores eléctricos y gas"
    },
    { 
      icon: Droplets, 
      titleEn: "Drain Cleaning", 
      titleEs: "Destapado de Drenajes",
      descEn: "Professional drain cleaning and unclogging services",
      descEs: "Limpieza profesional y destapado de drenajes"
    },
    { 
      icon: Home, 
      titleEn: "Residential Plumbing", 
      titleEs: "Plomería Residencial",
      descEn: "Complete plumbing solutions for your home",
      descEs: "Soluciones completas de plomería para su hogar"
    }
  ];

  const whyChooseUs = [
    { 
      icon: Shield, 
      titleEn: "Licensed & Insured", 
      titleEs: "Licenciados y Asegurados",
      descEn: "Fully licensed and insured professionals",
      descEs: "Profesionales totalmente licenciados y asegurados"
    },
    { 
      icon: Clock, 
      titleEn: "Fast Response", 
      titleEs: "Respuesta Rápida",
      descEn: "Quick arrival times for all service calls",
      descEs: "Tiempos de llegada rápidos para todas las llamadas"
    },
    { 
      icon: DollarSign, 
      titleEn: "Honest Pricing", 
      titleEs: "Precios Honestos",
      descEn: "Transparent pricing with no hidden fees",
      descEs: "Precios transparentes sin cargos ocultos"
    },
    { 
      icon: Award, 
      titleEn: "Quality Guaranteed", 
      titleEs: "Calidad Garantizada",
      descEn: "All work backed by our satisfaction guarantee",
      descEs: "Todo el trabajo respaldado por nuestra garantía"
    }
  ];
  
  return (
    <section id="servicios" className="py-12 md:py-20 bg-white" data-testid="services-section">
      <div className="px-4 md:px-8 lg:px-16">
        {/* Section Header - SEO Optimized H2 */}
        <div className="text-center mb-10 md:mb-14">
          <p className="text-xs md:text-sm text-[#D4AF37] uppercase tracking-widest mb-2 font-semibold">
            {language === 'en' ? 'Hampton Roads Plumbing Services' : 'Servicios de Plomería Hampton Roads'}
          </p>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#0F172A]" style={{ fontFamily: "'Playfair Display', serif" }}>
            {language === 'en' ? 'Virginia Beach Plumbing & Drain Repairs' : 'Plomería y Reparación de Drenajes Virginia Beach'}
          </h2>
          <p className="text-slate-600 mt-3 max-w-2xl mx-auto text-sm md:text-base">
            {language === 'en' 
              ? 'Trusted plumbing services in Norfolk, Chesapeake, and all Hampton Roads VA. Licensed & insured professionals.'
              : 'Servicios de plomería confiables en Norfolk, Chesapeake y todo Hampton Roads VA. Profesionales licenciados y asegurados.'}
          </p>
        </div>

        {/* Services Grid - Mobile First (Vertical Stack) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-6 mb-12 md:mb-16">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <div 
                key={index}
                className="bg-slate-50 rounded-xl p-5 md:p-6 text-center hover:bg-[#0F172A] hover:text-white group transition-all duration-300 cursor-pointer"
                data-testid={`service-card-${index}`}
              >
                <div className="w-14 h-14 md:w-16 md:h-16 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-white group-hover:scale-110 transition-all duration-300">
                  <Icon className="w-7 h-7 md:w-8 md:h-8 text-white group-hover:text-[#D4AF37]" strokeWidth={1.5} />
                </div>
                <h3 className="text-base md:text-lg font-semibold mb-2 group-hover:text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {language === 'en' ? service.titleEn : service.titleEs}
                </h3>
                <p className="text-xs md:text-sm text-slate-500 group-hover:text-slate-300 leading-relaxed">
                  {language === 'en' ? service.descEn : service.descEs}
                </p>
              </div>
            );
          })}
        </div>

        {/* Why Choose Us Section - SEO Optimized */}
        <div className="bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-2xl p-6 md:p-10 lg:p-12">
          <div className="text-center mb-8 md:mb-10">
            <p className="text-xs md:text-sm text-[#D4AF37] uppercase tracking-widest mb-2 font-semibold">
              {language === 'en' ? 'Norfolk & Chesapeake Plumbing Specialists' : 'Especialistas en Plomería Norfolk y Chesapeake'}
            </p>
            <h3 className="text-xl md:text-2xl lg:text-3xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
              {language === 'en' ? 'The Pellon Morel Solutions Difference' : 'La Diferencia Pellon Morel Solutions'}
            </h3>
          </div>

          {/* Why Choose Us Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8 md:mb-10">
            {whyChooseUs.map((item, index) => {
              const Icon = item.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-12 h-12 md:w-14 md:h-14 bg-[#D4AF37] rounded-full flex items-center justify-center mx-auto mb-3">
                    <Icon className="w-6 h-6 md:w-7 md:h-7 text-white" strokeWidth={1.5} />
                  </div>
                  <h4 className="text-sm md:text-base font-semibold text-white mb-1">
                    {language === 'en' ? item.titleEn : item.titleEs}
                  </h4>
                  <p className="text-xs text-slate-400 hidden md:block">
                    {language === 'en' ? item.descEn : item.descEs}
                  </p>
                </div>
              );
            })}
          </div>

          {/* CTA Button */}
          <div className="text-center">
            <a 
              href="tel:+17576503192" 
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-white px-6 md:px-8 py-3 md:py-4 text-sm md:text-base uppercase tracking-widest hover:bg-[#B5952F] transition-all duration-300 rounded-lg font-semibold"
              data-testid="emergency-call-button"
            >
              <Phone className="w-4 h-4 md:w-5 md:h-5" />
              {language === 'en' ? 'Call Now' : 'Llamar Ahora'}
            </a>
            <p className="text-slate-400 text-xs md:text-sm mt-3">
              {language === 'en' ? '24/7 Emergency Service Available' : 'Servicio de Emergencia 24/7 Disponible'}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;